// packages/ui/src/store/sessionManifest.ts
import type { PlatformBridge } from '@slate/core';
import { useDocStore, type OpenDoc } from './docStore';
import { toolsToSnapshot, type ToolsSnapshot } from '../tools/toolsStore';
import { draftRegistry } from './draftId';

export interface SessionTab {
  id: string;
  kind: 'untitled' | 'file' | 'image';
  path?: string;
  name: string;
  /** Present when the doc has an unsaved draft (dirty). */
  draftId?: string;
  dirty: boolean;
  /** Per-doc language override (a `LANGUAGE_CHOICES` key); restored on launch. */
  langOverride?: string;
}

export interface SessionManifest {
  version: 1;
  workspaceRoots: { id: string; path?: string; name: string }[];
  tabs: SessionTab[];
  activeId: string | null;
  tools: ToolsSnapshot;
}

function tabKind(doc: OpenDoc): SessionTab['kind'] {
  if (doc.kind === 'image') return 'image';
  return doc.ref?.path ? 'file' : 'untitled';
}

export function buildManifest(): SessionManifest {
  const s = useDocStore.getState();
  const tabs: SessionTab[] = s.docs.map((doc) => {
    const kind = tabKind(doc);
    const tab: SessionTab = {
      id: doc.id,
      kind,
      name: doc.ref?.name ?? 'Untitled',
      dirty: doc.dirty,
    };
    if (doc.ref?.path) tab.path = doc.ref.path;
    if (doc.langOverride) tab.langOverride = doc.langOverride;
    // A draft exists for any dirty doc (untitled OR file-backed). Image docs
    // never carry text drafts.
    if (doc.dirty && kind !== 'image') tab.draftId = draftRegistry.idFor(doc.id);
    return tab;
  });
  return {
    version: 1,
    workspaceRoots: s.workspaceRoots.map((r) => ({ id: r.id, path: r.path, name: r.name })),
    tabs,
    activeId: s.activeId,
    tools: toolsToSnapshot(),
  };
}

export async function writeManifest(platform: PlatformBridge): Promise<void> {
  if (!platform.session) return;
  try {
    await platform.session.write(JSON.stringify(buildManifest()));
  } catch {
    /* best-effort */
  }
}

export async function readManifest(platform: PlatformBridge): Promise<SessionManifest | null> {
  if (!platform.session) return null;
  try {
    const raw = await platform.session.read();
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionManifest;
    if (parsed?.version !== 1 || !Array.isArray(parsed.tabs)) return null;
    return parsed;
  } catch {
    return null;
  }
}
